// course-ai-field.component.ts
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ElementRef,
  AfterViewInit,
} from '@angular/core';
import { AiService } from '../../services/ai.service';
import { lastValueFrom } from 'rxjs';
import { AiFieldQueueService } from '../../services/ai-field-queue.service';
import { CourseAiFieldHistoryService } from '../../services/course-ai-field-history.service';

export type FieldType =
  | 'textarea'
  | 'textbox'
  | 'multiselect'
  | 'singleselect'
  | 'keyvalue'
  | 'container'
  | 'editor'
  | 'markdown';

export interface KeyValuePair {
  key: string;
  value: string;
  id?: string;
}

export interface MultiselectOption {
  id: string;
  label: string;
  selected?: boolean;
}

export interface SingleSelectOption {
  id: string;
  label: string;
}

@Component({
  selector: 'app-course-ai-field',
  templateUrl: './course-ai-field.component.html',
  styleUrls: ['./course-ai-field.component.css'],
})
export class CourseAiFieldComponent
  implements OnInit, OnDestroy, AfterViewInit {
  @Input() textViewClasses: string = 'text-[16px]';
  @Output() onStartGenerating = new EventEmitter<void>();
  @Output() onFinishGenerating = new EventEmitter<any>();

  private _trigger = null;
  @Input() id: string = this.generateUniqueString();
  @Input('triggerGenerate') set triggerGenerate(value: number) {
    if (value && value !== this._trigger && value !== 0) {
      this._trigger = value;
      this.onGenerate();
    }
  }

  @Output() triggerGenerateChange = new EventEmitter<number>();

  @Input() label!: string;
  @Input() placeholder!: string;
  @Input() prompt!: string; // AI suggestion
  @Input() hideAi: boolean = false; // AI suggestion
  @Input() fieldType: FieldType = 'textarea';

  // For textarea and textbox
  @Input() value: string = '';
  @Output() valueChange = new EventEmitter<string>();

  // For multiselect
  @Input() multiselectOptions: MultiselectOption[] = [];
  @Input() multiselectValue: string[] = [];
  @Output() multiselectValueChange = new EventEmitter<string[]>();

  // For single select
  @Input() singleSelectOptions: SingleSelectOption[] = [];
  @Input() singleSelectValue: string = '';
  @Output() singleSelectValueChange = new EventEmitter<string>();

  // For key-value pairs
  @Input() keyValuePairs: KeyValuePair[] = [];
  @Output() keyValuePairsChange = new EventEmitter<KeyValuePair[]>();

  // Additional options
  @Input() multiselectPlaceholder: string = 'Select options...';
  @Input() singleSelectPlaceholder: string = 'Select an option...';
  @Input() keyPlaceholder: string = 'Enter key...';
  @Input() valuePlaceholder: string = 'Enter value...';

  // Editor-specific options
  @Input() stickEditorHead: boolean = false;
  @Input() hideBuiltInAiButton: boolean = true;

  // Validation
  @Input() isValid: boolean = true;
  @Input() errorMessage: string = '';
  @Input() required: boolean = false;

  @ViewChild('textareaRef') textareaRef!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('textboxRef') textboxRef!: ElementRef<HTMLInputElement>;

  @Output() catchController = new EventEmitter<any>();

  @Input() aiGenerateOverrideFunction?: () => Promise<any>;

  isEditing = false;
  @Input() isGenerating: boolean = false;
  @Output() isGeneratingChange = new EventEmitter<boolean>();
  @Input() disableGenerate: boolean = false;

  public aiStatus$ = this.aiService.isReady$;
  public get hasAiError() {
    return this.aiService.hasInitializationError;
  }
  public hasGenerationError = false;

  showSaveAnimation = false;
  history: any[] = [];
  redoStack: any[] = [];
  originalValue: any; // Store original value when entering edit mode

  private resizeObserver?: ResizeObserver;
  private saveAnimationTimeout?: any;
  private editorController: any;

  constructor(
    private aiService: AiService,
    private aiQueue: AiFieldQueueService,
    private historyService: CourseAiFieldHistoryService,
  ) { }

  private generateUniqueString(): string {
    return Math.random().toString(36).substring(2, 8) + Date.now();
  }

  private get historyKey(): string {
    return this.id || this.label || '';
  }

  private saveHistoryState(): void {
    const key = this.historyKey;
    if (key) {
      this.historyService.saveState(key, this.history, this.redoStack);
    }
  }

  ngOnInit(): void {
    const key = this.historyKey;
    if (key) {
      const savedState = this.historyService.getHistory(key);
      if (savedState) {
        this.history = savedState.history;
        this.redoStack = savedState.redoStack;
      }
    }
  }

  ngAfterViewInit(): void {
    // Set up auto-resize for textarea
    if (this.fieldType === 'textarea' && this.textareaRef?.nativeElement) {
      this.setupTextareaResize();
    }
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.saveAnimationTimeout) {
      clearTimeout(this.saveAnimationTimeout);
    }
  }

  // === Editor Methods ===
  onCatchController(controller: any): void {
    this.editorController = controller;
    this.catchController.emit(controller);
  }

  // Add this inside CourseAiFieldComponent class
  get hasContent(): boolean {
    // if string remove the html tags from it with regex
    const val = this.getCurrentValue();
    return this.hasValue(val) || this.historyTotal > 1;
  }

  // === Value Management ===
  getCurrentValue(): any {
    switch (this.fieldType) {
      case 'textarea':
      case 'textbox':
      case 'editor':
      case 'markdown':
        return this.value;
      case 'multiselect':
        return [...this.multiselectValue];
      case 'singleselect':
        return this.singleSelectValue;
      case 'keyvalue':
        return this.keyValuePairs.map((pair) => ({ ...pair }));
      default:
        return '';
    }
  }

  setCurrentValue(newValue: any): void {
    switch (this.fieldType) {
      case 'textarea':
      case 'textbox':
      case 'editor':
      case 'markdown':
        this.value = newValue || '';
        this.valueChange.emit(this.value);
        break;
      case 'multiselect':
        this.multiselectValue = Array.isArray(newValue) ? newValue : [];
        this.multiselectValueChange.emit(this.multiselectValue);
        break;
      case 'singleselect':
        this.singleSelectValue = newValue || '';
        this.singleSelectValueChange.emit(this.singleSelectValue);
        break;
      case 'keyvalue':
        this.keyValuePairs = Array.isArray(newValue) ? newValue : [];
        this.keyValuePairsChange.emit(this.keyValuePairs);
        break;
    }
  }

  hasValue(value: any): boolean {
    switch (this.fieldType) {
      case 'textarea':
      case 'textbox':
      case 'editor':
      case 'markdown':
      case 'singleselect':
        const cleanedValue = value?.replace(/<[^>]*>/g, '')?.trim();
        return cleanedValue && cleanedValue.length > 0;
      case 'multiselect':
        return Array.isArray(value) && value.length > 0;
      case 'keyvalue':
        return Array.isArray(value) && value.length > 0;
      default:
        return false;
    }
  }

  // === State Getters ===
  get canUndo(): boolean {
    return this.history.length > 0;
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  get historyPosition(): number {
    return this.history.length + 1;
  }

  get historyTotal(): number {
    return this.history.length + 1 + this.redoStack.length;
  }

  // === Editing Methods ===
  toggleEdit(): void {
    if (this.isEditing) {
      this.saveEdit();
    } else {
      this.enterEditMode();
    }
  }

  enterEditMode(): void {
    this.originalValue = this.deepClone(this.getCurrentValue());
    this.isEditing = true;

    // Focus appropriate element after view update
    setTimeout(() => {
      this.focusEditElement();
    }, 50);
  }

  private focusEditElement(): void {
    if ((this.fieldType === 'textarea' || this.fieldType === 'markdown') && this.textareaRef?.nativeElement) {
      const textarea = this.textareaRef.nativeElement;
      textarea.focus();
      textarea.setSelectionRange(textarea.value.length, textarea.value.length);
      this.autoResizeTextarea();
    } else if (this.fieldType === 'textbox' && this.textboxRef?.nativeElement) {
      this.textboxRef.nativeElement.focus();
    } else if (this.fieldType === 'editor' && this.editorController) {
      // Focus editor if controller is available
      this.editorController.focus?.();
    }
  }

  saveEdit(): void {
    const currentValue = this.getCurrentValue();
    if (!this.valuesEqual(currentValue, this.originalValue)) {
      this.addToHistory(this.originalValue);
      this.redoStack = []; // Clear redo stack on new changes
      this.saveHistoryState();
      this.showSaveSuccess();
    }
    this.isEditing = false;
  }

  cancelEdit(): void {
    this.setCurrentValue(this.originalValue);
    this.isEditing = false;
  }

  // === AI Generation ===
  async onGenerate(): Promise<void> {
    if (this.isGenerating || this.disableGenerate) return;
    this.onStartGenerating.emit();

    this.isGenerating = true;
    this.hasGenerationError = false;
    this.isGeneratingChange.emit(this.isGenerating);
    try {
      await this.aiQueue.enqueue(this.id, async () => {
        const oldValue = this.deepClone(this.getCurrentValue());
        if (this.aiGenerateOverrideFunction) {
          const result = await this.aiGenerateOverrideFunction();
          this.setCurrentValue(result);
        } else {
          if (!this.prompt?.trim()) {
            console.warn('AI generation skipped: empty prompt');
            return;
          }

          const generatedText = await lastValueFrom(
            this.aiService.getCompletion(this.prompt, {
              stream: false,
              model: this.aiService.getCurrentModel(),
            }),
          );

          // Parse AI response based on field type
          const parsedValue = this.parseAIResponse(generatedText);
          this.setCurrentValue(parsedValue);
        }

        console.log('Adding To Histroy:', oldValue);
        this.addToHistory(oldValue);
        this.redoStack = [];
        this.saveHistoryState();

        this.showSaveSuccess();
      });
    } catch (error) {
      this.hasGenerationError = true;
      console.error('AI generation failed:', error);
    } finally {
      this.isGenerating = false;
      this.isGeneratingChange.emit(this.isGenerating);
      this._trigger = 0;
      this.triggerGenerateChange.emit(this._trigger);
      this.onFinishGenerating.emit(this.getCurrentValue());
    }
  }

  private parseAIResponse(response: string): any {
    try {
      switch (this.fieldType) {
        case 'textarea':
        case 'textbox':
        case 'editor':
        case 'markdown':
          return response;
        case 'multiselect':
          // Try to parse as JSON array, fallback to comma-separated
          try {
            const parsed = JSON.parse(response);
            return Array.isArray(parsed)
              ? parsed
              : response.split(',').map((s) => s.trim());
          } catch {
            return response
              .split(',')
              .map((s) => s.trim())
              .filter((s) => s.length > 0);
          }
        case 'singleselect':
          // Return the first valid option ID or the trimmed response
          const trimmedResponse = response.trim();
          const matchingOption = this.singleSelectOptions.find(
            (opt) =>
              opt.id === trimmedResponse || opt.label === trimmedResponse,
          );
          return matchingOption ? matchingOption.id : trimmedResponse;
        case 'keyvalue':
          // Try to parse as JSON object/array, fallback to key:value format
          try {
            const parsed = JSON.parse(response);
            if (Array.isArray(parsed)) {
              return parsed.map((item, index) => ({
                id: this.generateUniqueString(),
                key: item.key || `Key ${index + 1}`,
                value: item.value || item.toString(),
              }));
            } else if (typeof parsed === 'object') {
              return Object.entries(parsed).map(([key, value]) => ({
                id: this.generateUniqueString(),
                key,
                value: value?.toString() || '',
              }));
            }
          } catch {
            // Parse key:value or key=value format
            return response
              .split('\n')
              .map((line) => line.trim())
              .filter((line) => line.length > 0)
              .map((line) => {
                const separator = line.includes(':') ? ':' : '=';
                const [key, ...valueParts] = line.split(separator);
                return {
                  id: this.generateUniqueString(),
                  key: key?.trim() || '',
                  value: valueParts.join(separator).trim() || '',
                };
              })
              .filter((pair) => pair.key.length > 0);
          }
          return [];
        default:
          return response;
      }
    } catch (error) {
      console.error('Failed to parse AI response:', error);
      return this.fieldType === 'textarea' ||
        this.fieldType === 'textbox' ||
        this.fieldType === 'editor' ||
        this.fieldType === 'markdown'
        ? response
        : '';
    }
  }

  // === Input Handling ===
  onInputChange(newValue: string): void {
    if (
      this.fieldType === 'textarea' ||
      this.fieldType === 'textbox' ||
      this.fieldType === 'editor' ||
      this.fieldType === 'markdown'
    ) {
      this.value = newValue;
      this.valueChange.emit(newValue);
      if (this.fieldType === 'textarea' || this.fieldType === 'markdown') {
        this.autoResizeTextarea();
      }
    }
  }

  onMultiselectChange(newValue: string[]): void {
    newValue = newValue.map((value) => {
      return (value as any)?.label || value;
    });
    this.multiselectValue = newValue;
    this.multiselectValueChange.emit(newValue);
  }

  onSingleSelectChange(newValue: string): void {
    const actualValue = (newValue as any)?.label || newValue;
    this.singleSelectValue = actualValue;
    this.singleSelectValueChange.emit(actualValue);
  }

  isOptionSelected(optionId: string): boolean {
    return this.multiselectValue.includes(optionId);
  }

  getSelectedOptionsText(): string {
    const selected = this.multiselectOptions.filter((option) =>
      this.multiselectValue.includes(option.id),
    );
    return selected.length > 0
      ? selected.map((option) => option.label).join(', ')
      : this.multiselectPlaceholder;
  }

  getSelectedOptionLabel(): string {
    const selected = this.singleSelectOptions.find(
      (option) => option.id === this.singleSelectValue,
    );
    return (
      selected?.label || this.singleSelectValue || this.singleSelectPlaceholder
    );
  }

  // === Key-Value Methods ===
  addKeyValuePair(): void {
    this.keyValuePairs = [
      ...this.keyValuePairs,
      {
        id: this.generateUniqueString(),
        key: '',
        value: '',
      },
    ];
  }

  removeKeyValuePair(id: string): void {
    this.keyValuePairs = this.keyValuePairs.filter((pair) => pair.id !== id);
  }

  updateKeyValuePair(
    id: string,
    field: 'key' | 'value',
    newValue: string,
  ): void {
    this.keyValuePairs = this.keyValuePairs.map((pair) =>
      pair.id === id ? { ...pair, [field]: newValue } : pair,
    );
  }

  // === Undo/Redo ===
  undo(): void {
    if (this.canUndo) {
      this.redoStack.push(this.deepClone(this.getCurrentValue()));
      const previousValue = this.history.pop();
      this.setCurrentValue(previousValue);
      this.saveHistoryState();
    }
  }

  redo(): void {
    if (this.canRedo) {
      this.history.push(this.deepClone(this.getCurrentValue()));
      const nextValue = this.redoStack.pop();
      this.setCurrentValue(nextValue);
      this.saveHistoryState();
    }
  }

  // === Helper Methods ===
  private addToHistory(val: any): void {
    if (
      val !== undefined &&
      val !== null &&
      this.hasValue(val) &&
      !this.valuesEqual(val, this.getCurrentValue())
    ) {
      this.history.push(this.deepClone(val));

      // Limit history size to prevent memory issues
      if (this.history.length > 50) {
        this.history = this.history.slice(-50);
      }
      this.saveHistoryState();
    }
  }

  private deepClone(obj: any): any {
    return JSON.parse(JSON.stringify(obj));
  }

  private valuesEqual(val1: any, val2: any): boolean {
    return JSON.stringify(val1) === JSON.stringify(val2);
  }

  private showSaveSuccess(): void {
    this.showSaveAnimation = true;

    if (this.saveAnimationTimeout) {
      clearTimeout(this.saveAnimationTimeout);
    }

    this.saveAnimationTimeout = setTimeout(() => {
      this.showSaveAnimation = false;
    }, 1000);
  }

  private setupTextareaResize(): void {
    if (!this.textareaRef?.nativeElement) return;

    // Set up ResizeObserver for better performance
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.autoResizeTextarea();
      });
      this.resizeObserver.observe(this.textareaRef.nativeElement);
    }
  }

  private autoResizeTextarea(): void {
    if (
      !this.textareaRef?.nativeElement ||
      !this.isEditing ||
      (this.fieldType !== 'textarea' && this.fieldType !== 'markdown')
    )
      return;

    const textarea = this.textareaRef.nativeElement;

    // Reset height to get accurate scrollHeight
    textarea.style.height = 'auto';

    // Set new height based on content, with min and max constraints
    const minHeight = 140;
    const maxHeight = 400;
    const scrollHeight = Math.max(
      minHeight,
      Math.min(maxHeight, textarea.scrollHeight),
    );

    textarea.style.height = `${scrollHeight}px`;

    // Add scrollbar if content exceeds max height
    textarea.style.overflowY = scrollHeight >= maxHeight ? 'auto' : 'hidden';
  }

  // === Keyboard Shortcuts ===
  onKeyDown(event: KeyboardEvent): void {
    if (!this.isEditing) return;

    // Don't handle shortcuts for editor field type (let the editor handle them)
    if (this.fieldType === 'editor') return;

    // Cmd/Ctrl + Enter to save
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      this.saveEdit();
    }

    // Escape to cancel
    if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelEdit();
    }

    // Cmd/Ctrl + Z for undo
    if (
      (event.metaKey || event.ctrlKey) &&
      event.key === 'z' &&
      !event.shiftKey
    ) {
      event.preventDefault();
      this.undo();
    }

    // Cmd/Ctrl + Shift + Z for redo
    if (
      (event.metaKey || event.ctrlKey) &&
      event.key === 'z' &&
      event.shiftKey
    ) {
      event.preventDefault();
      this.redo();
    }
  }

  // === Template Helper Methods ===
  trackByPairId(index: number, pair: KeyValuePair): string {
    return pair.id || index.toString();
  }

  isHTML(val: string): boolean {
    if (!val) return false;
    const trimmed = val.trim();
    return trimmed.startsWith('<') && trimmed.endsWith('>');
  }

  // Helper method to get option label by ID (alternative to find pipe)
  getOptionLabel(optionId: string): string {
    const option = this.multiselectOptions.find((opt) => opt.id === optionId);
    return option?.label || optionId;
  }
}
